import { type FormEvent, useState } from 'react';
import { ArrowLeft, Check, KeyRound, LoaderCircle, UserPlus } from 'lucide-react';
import type { AccessRequestInput } from '@forq/contracts';

/**
 * Die zwei Nebenwege des Logins: Passwort vergessen und Zugang anfragen.
 *
 * Beide antworten BEWUSST unabhaengig davon, ob es die Adresse gibt. Ein Formular, das "kenne ich
 * nicht" sagt, ist ein Werkzeug zum Aufzaehlen gueltiger Konten -- und diese Seite ist oeffentlich.
 *
 * ACHTUNG BETRIEB: der Mailversand laeuft aktuell ueber Supabases eingebauten Dienst. Der ist
 * ausdruecklich nur zum Entwickeln (wenige Mails pro Stunde). Vor dem Echtbetrieb MUSS eigenes
 * SMTP hinterlegt werden (Supabase -> Auth -> SMTP Settings), sonst faellt "Passwort vergessen"
 * genau dann aus, wenn morgens drei Leute gleichzeitig danach fragen. Gilt genauso fuer die
 * Einladungen im Adminpanel.
 */

interface ResetViewProps {
  readonly onSubmit: (email: string) => Promise<void>;
  readonly onBack: () => void;
}

export function PasswordResetView({ onSubmit, onBack }: ResetViewProps) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(email.trim());
      setSent(true);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Die Anfrage konnte nicht gesendet werden.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="forq-login-done">
        <div className="forq-login-done__icon"><Check size={20} /></div>
        <p>
          Wenn ein Konto zu <strong>{email}</strong> existiert, ist eine E-Mail mit einem Link zum
          Zurücksetzen unterwegs. Der Link gilt eine Stunde.
        </p>
        <p className="forq-login-done__hint">Nichts angekommen? Schau im Spam-Ordner nach.</p>
        <button type="button" className="forq-bootstrap__secondary" onClick={onBack}>
          <ArrowLeft size={16} /> Zurück zur Anmeldung
        </button>
      </div>
    );
  }

  return (
    <form className="forq-login-form" onSubmit={submit}>
      <label>
        E-Mail-Adresse
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="username"
          required
          autoFocus
        />
      </label>
      {error && <div className="forq-bootstrap__error" role="alert">{error}</div>}
      <button className="forq-bootstrap__primary" type="submit" disabled={busy}>
        {busy ? <LoaderCircle className="forq-spin" size={18} /> : <KeyRound size={18} />}
        {busy ? 'Wird gesendet …' : 'Link zum Zurücksetzen senden'}
      </button>
      <button type="button" className="forq-login-link" onClick={onBack}>
        <ArrowLeft size={14} /> Zurück zur Anmeldung
      </button>
    </form>
  );
}

interface AccessViewProps {
  readonly onSubmit: (input: AccessRequestInput) => Promise<void>;
  readonly onBack: () => void;
}

export function AccessRequestView({ onSubmit, onBack }: AccessViewProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ email: email.trim(), displayName: displayName.trim(), note: note.trim() });
      setSent(true);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Die Anfrage konnte nicht gesendet werden.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="forq-login-done">
        <div className="forq-login-done__icon"><Check size={20} /></div>
        <p>
          Deine Anfrage liegt jetzt bei der Administration. Du bekommst eine Nachricht an
          <strong> {email}</strong>, sobald sie entschieden ist.
        </p>
        <button type="button" className="forq-bootstrap__secondary" onClick={onBack}>
          <ArrowLeft size={16} /> Zurück zur Anmeldung
        </button>
      </div>
    );
  }

  return (
    <form className="forq-login-form" onSubmit={submit}>
      <label>
        Vor- und Nachname
        <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required autoFocus minLength={2} />
      </label>
      <label>
        Dienstliche E-Mail-Adresse
        <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" required />
      </label>
      <label>
        Wofür brauchst du den Zugang? <span className="forq-login-optional">optional</span>
        <textarea
          rows={3}
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          placeholder="z. B. Abteilung, Aufgabe, ab wann"
        />
      </label>
      {error && <div className="forq-bootstrap__error" role="alert">{error}</div>}
      <button className="forq-bootstrap__primary" type="submit" disabled={busy}>
        {busy ? <LoaderCircle className="forq-spin" size={18} /> : <UserPlus size={18} />}
        {busy ? 'Wird gesendet …' : 'Zugang anfragen'}
      </button>
      <button type="button" className="forq-login-link" onClick={onBack}>
        <ArrowLeft size={14} /> Zurück zur Anmeldung
      </button>
    </form>
  );
}
